import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Sun, Moon, Eye, EyeOff } from 'lucide-react';
import { LogoIcon } from '../components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee'
  });
  const [rememberMe, setRememberMe] = useState(true); // TC-AUTH-033: Remember Me checkbox
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {
      email: '',
      password: '',
      full_name: ''
    };
    let isValid = true;

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Invalid email format';
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    // Full name validation (only for registration)
    if (!isLogin && !formData.full_name.trim()) {
      errors.full_name = 'Full name is required';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Setup demo accounts for production
  const setupDemoAccounts = async () => {
    setSettingUp(true);
    try {
      const response = await axios.post(`${API}/auth/setup-demo-accounts`);
      const { results } = response.data;
      if (results.created.length > 0) {
        toast.success(`Created ${results.created.length} demo accounts!`);
      } else if (results.existing.length > 0) {
        toast.info('Demo accounts already exist. You can login now!');
      }
    } catch (error) {
      console.error('Setup error:', error);
      toast.error('Failed to setup demo accounts');
    } finally {
      setSettingUp(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    // Store signup flag in sessionStorage for the callback
    sessionStorage.setItem('google_signup_data', JSON.stringify({
      isSignup: !isLogin,
      companyName: null,
      companySize: null
    }));
    
    // Redirect to Emergent Auth with dynamic redirect URL.
    // Iter88u — Auth service URL is env-driven so production deploys can
    // override if needed (e.g., a different OAuth proxy domain).
    // Iter95 — Removed hardcoded fallback per deployment health check
    // rule. `REACT_APP_AUTH_SERVICE_URL` MUST be set in frontend/.env.
    const authServiceUrl = process.env.REACT_APP_AUTH_SERVICE_URL;
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${authServiceUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationErrors({ email: '', password: '', full_name: '' });
    setErrorMessage('');
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);

      // TC-AUTH-033: Store in localStorage (persists) or sessionStorage (cleared on browser close)
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('hrms_token', response.data.token);
      storage.setItem('hrms_user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      
      toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed. Please check your credentials.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1610214094153-b98142129376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwaW50ZXJpb3IlMjBtYWxheXNpYXxlbnwwfHx8fDE3NjU3MzA1MTh8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-emerald-900/80" />
        <div className="relative h-full flex flex-col justify-center px-12 text-white">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center space-x-3 mb-8 hover:opacity-80 transition-opacity"
          >
            <LogoIcon className="h-12 w-12" />
            <div className="flex items-baseline space-x-1">
              <span className="text-4xl font-bold">MyHR</span>
              <span className="text-4xl font-bold text-emerald-400">Orbit</span>
            </div>
          </button>
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            {t('login.title').split(' ').slice(0, 2).join(' ')}<br />{t('login.title').split(' ').slice(2, 4).join(' ')}<br />{t('login.title').split(' ').slice(4).join(' ')}
          </h1>
          <p className="text-xl text-slate-300 max-w-md">
            {t('login.subtitle')}
          </p>
          
          {/* All Login Credentials - Consolidated */}
          <div className="mt-8 space-y-3 max-w-md">
            <h3 className="text-sm font-bold text-white flex items-center">
              <span className="mr-2">🔐</span> Test Login Credentials <span className="text-xs text-slate-400 ml-2">(Click to auto-fill)</span>
            </h3>
            
            {/* HR Admin - Full Access */}
            <button
              onClick={() => setFormData({ ...formData, email: 'hr@myhrorbit.com', password: 'password123' })}
              className="w-full text-left bg-gradient-to-r from-emerald-900/60 to-teal-900/60 border border-emerald-500/40 rounded-xl p-3 hover:border-emerald-400 hover:scale-[1.02] transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-emerald-300">HR ADMIN</span>
                <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full">Full HR Access</span>
              </div>
              <div className="bg-emerald-950/50 rounded-lg p-2">
                <p className="text-sm text-white font-mono">hr@myhrorbit.com</p>
                <p className="text-xs text-emerald-300">Password: <span className="font-mono text-white">password123</span></p>
              </div>
            </button>

            {/* Super Admin - Platform Access */}
            <button
              onClick={() => setFormData({ ...formData, email: 'superadmin@myhrorbit.com', password: 'SuperAdmin@123' })}
              className="w-full text-left bg-gradient-to-r from-violet-900/60 to-purple-900/60 border border-violet-500/40 rounded-xl p-3 hover:border-violet-400 hover:scale-[1.02] transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-violet-300">SUPER ADMIN</span>
                <span className="text-[10px] bg-violet-500 text-white px-2 py-0.5 rounded-full">Platform Admin</span>
              </div>
              <div className="bg-violet-950/50 rounded-lg p-2">
                <p className="text-sm text-white font-mono">superadmin@myhrorbit.com</p>
                <p className="text-xs text-violet-300">Password: <span className="font-mono text-white">SuperAdmin@123</span></p>
              </div>
            </button>

            {/* Employee Access */}
            <button
              onClick={() => setFormData({ ...formData, email: 'employee1@myhrorbit.com', password: 'Employee@123' })}
              className="w-full text-left bg-gradient-to-r from-blue-900/60 to-cyan-900/60 border border-blue-500/40 rounded-xl p-3 hover:border-blue-400 hover:scale-[1.02] transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-blue-300">EMPLOYEE</span>
                <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded-full">Self-Service</span>
              </div>
              <div className="bg-blue-950/50 rounded-lg p-2">
                <p className="text-sm text-white font-mono">employee1@myhrorbit.com</p>
                <p className="text-xs text-blue-300">Password: <span className="font-mono text-white">Employee@123</span></p>
              </div>
            </button>

            {/* Beta Access */}
            <div className="bg-amber-900/30 border border-amber-600/40 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-amber-300">BETA ACCESS PASSWORD</span>
              </div>
              <p className="text-lg font-mono text-white tracking-wider">8269</p>
            </div>

            {/* Setup Demo Accounts Button */}
            <button
              onClick={setupDemoAccounts}
              disabled={settingUp}
              className="w-full text-center bg-slate-800/50 border border-slate-600/40 rounded-xl p-3 hover:border-slate-500 hover:bg-slate-700/50 transition-all cursor-pointer disabled:opacity-50"
            >
              <span className="text-xs font-bold text-slate-300">
                {settingUp ? '⏳ Setting up...' : '🚀 First time? Click to Setup Demo Accounts'}
              </span>
            </button>
          </div>

          <div className="mt-6 space-y-3">
            <div className="flex items-center space-x-3">
              <div className="h-5 w-5 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0">
                <span className="text-slate-900 text-xs font-bold">✓</span>
              </div>
              <p className="text-sm text-slate-300">{t('login.compliance')} - EPF, SOCSO, EIS, PCB</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="h-5 w-5 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0">
                <span className="text-slate-900 text-xs font-bold">✓</span>
              </div>
              <p className="text-sm text-slate-300">Payroll & Payslip Generation</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="h-5 w-5 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0">
                <span className="text-slate-900 text-xs font-bold">✓</span>
              </div>
              <p className="text-sm text-slate-300">Leave, Claims & Attendance Management</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-slate-50 dark:bg-slate-900">
        <div className="w-full max-w-md">
          {/* Theme Toggle & Language Selector - Top Right */}
          <div className="flex justify-end items-center gap-2 mb-4">
            <LanguageSelector variant="light" />
            <button
              onClick={toggleTheme}
              className="p-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all duration-200 group"
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5 text-slate-700 group-hover:text-slate-900" />
              ) : (
                <Sun className="h-5 w-5 text-slate-300 group-hover:text-white" />
              )}
            </button>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                {isLogin ? t('login.welcome') : 'Create Account'}
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                {isLogin ? t('login.signInTo') : 'Get started with My HR Orbit'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
              {!isLogin && (
                <div>
                  <Label htmlFor="full_name" className="text-slate-700 dark:text-slate-300 font-medium">Full Name</Label>
                  <Input
                    id="full_name"
                    data-testid="full-name-input"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => {
                      setFormData({ ...formData, full_name: e.target.value });
                      if (validationErrors.full_name) setValidationErrors({ ...validationErrors, full_name: '' });
                    }}
                    className={`mt-1.5 h-11 rounded-xl border-slate-300 focus:border-emerald-500 focus:ring-emerald-500/20 ${validationErrors.full_name ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="Enter your full name"
                  />
                  {validationErrors.full_name && (
                    <p className="mt-1 text-sm text-red-500" data-testid="fullname-error">{validationErrors.full_name}</p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 font-medium">{t('login.email')}</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="text"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (validationErrors.email) setValidationErrors({ ...validationErrors, email: '' });
                    }}
                    className={`h-11 pl-10 rounded-xl border-slate-300 focus:border-emerald-500 focus:ring-emerald-500/20 ${validationErrors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="name@company.com"
                  />
                </div>
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-500" data-testid="email-error">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 font-medium">{t('login.password')}</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      if (validationErrors.password) setValidationErrors({ ...validationErrors, password: '' });
                    }}
                    className={`h-11 pl-10 pr-10 rounded-xl border-slate-300 focus:border-emerald-500 focus:ring-emerald-500/20 ${validationErrors.password ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-1 text-sm text-red-500" data-testid="password-error">{validationErrors.password}</p>
                )}
              </div>

              {/* TC-AUTH-033: Remember Me Checkbox */}
              {isLogin && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      data-testid="remember-me-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500/20 cursor-pointer"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors">
                      Remember me
                    </span>
                  </label>
                  {/* Iter88l — Forgot password link */}
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {!isLogin && (
                <div>
                  <Label htmlFor="role" className="text-slate-700 font-medium">Role</Label>
                  <select
                    id="role"
                    data-testid="role-select"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white"
                  >
                    <option value="employee">Employee</option>
                    <option value="hr_manager">HR Manager</option>
                    <option value="department_head">Department Head</option>
                    <option value="payroll_officer">Payroll Officer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}

              {/* Error Message Display */}
              {errorMessage && (
                <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm flex items-center gap-2" data-testid="login-error-message">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {errorMessage}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-btn"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-medium shadow-lg shadow-slate-900/20 transition-all active:scale-95 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>{isLogin ? t('login.signIn') : 'Create Account'}</span>
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-slate-800 text-slate-500">or continue with</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                data-testid="google-signin-btn"
                className="w-full h-11 flex items-center justify-center gap-3 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-xl font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-all shadow-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>{isLogin ? 'Sign in with Google' : 'Sign up with Google'}</span>
              </button>
            </form>

            <div className="mt-6 text-center">
              {isLogin ? (
                <p className="text-sm text-slate-600">
                  {t('login.noAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/signup')}
                    data-testid="goto-signup-btn"
                    className="text-emerald-600 hover:text-emerald-700 font-semibold"
                  >
                    {t('login.createAccount')}
                  </button>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  data-testid="toggle-auth-mode-btn"
                  className="text-sm text-slate-600 hover:text-slate-900 font-medium"
                >
                  Already have an account? Sign in
                </button>
              )}
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} My HR Orbit. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}